import CloseIcon from '@mui/icons-material/Close';
import DnsIcon from '@mui/icons-material/Dns';
import EmailIcon from '@mui/icons-material/Email';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GroupIcon from '@mui/icons-material/Group';
import SportsIcon from '@mui/icons-material/Sports';
import StorageIcon from '@mui/icons-material/Storage';
import WebIcon from '@mui/icons-material/Web';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { HowItWorksRole, HowItWorksStep, HowItWorksStepType } from '../../types/howItWorks';

const ROLE_ICONS: Record<HowItWorksRole, React.ReactNode> = {
  coach: <GroupIcon sx={{ fontSize: 14 }} />,
  athlete: <SportsIcon sx={{ fontSize: 14 }} />,
  guardian: <FamilyRestroomIcon sx={{ fontSize: 14 }} />,
  system: <DnsIcon sx={{ fontSize: 14 }} />,
};

const TYPE_CONFIG: Record<HowItWorksStepType, { label: string; icon: React.ReactNode }> = {
  UI: { label: 'UI', icon: <WebIcon sx={{ fontSize: 14 }} /> },
  Notification: { label: 'Notification', icon: <EmailIcon sx={{ fontSize: 14 }} /> },
  backend: { label: 'Backend', icon: <StorageIcon sx={{ fontSize: 14 }} /> },
};

const VIDEO_EXT = /\.(mp4|webm|mov|ogg)(\?|$)/i;

export interface StepDetailPanelProps {
  step: HowItWorksStep | null;
  onClose?: () => void;
}

const StepDetailPanel: React.FC<StepDetailPanelProps> = ({ step, onClose }) => {
  const theme = useTheme();

  if (!step) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Click a step to see details.</Typography>
      </Paper>
    );
  }

  const typeConfig = TYPE_CONFIG[step.type];
  const urlPrimary = step.urlPattern;
  const urlStr = Array.isArray(urlPrimary) ? urlPrimary.join(', ') : urlPrimary;

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography variant="h6" component="h2">
          {step.label}
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        <Chip size="small" icon={typeConfig.icon as React.ReactElement} label={typeConfig.label} sx={{ height: 22 }} />
        {step.roles.map((r) => (
          <Chip key={r} size="small" icon={ROLE_ICONS[r] as React.ReactElement} label={r} variant="outlined" sx={{ height: 22 }} />
        ))}
      </Box>

      {(urlStr || step.apiUrlPattern || step.internalName) && (
        <Box sx={{ mb: 1.5 }}>
          {urlStr != null && (
            <Typography variant="caption" color="text.secondary" component="div">
              Route / channel: <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{urlStr}</Typography>
            </Typography>
          )}
          {step.apiUrlPattern && (
            <Typography variant="caption" color="text.secondary" component="div">
              API: <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{step.apiUrlPattern}</Typography>
            </Typography>
          )}
          {step.internalName && (
            <Typography variant="caption" color="text.secondary" component="div">
              Internal: <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{step.internalName}</Typography>
            </Typography>
          )}
        </Box>
      )}

      {step.detail && (
        <Box sx={{ mb: 1.5, '& p': { mt: 0, mb: 0.5 }, '& p:last-child': { mb: 0 } }}>
          <ReactMarkdown>{step.detail}</ReactMarkdown>
        </Box>
      )}

      {step.media && step.media.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Media</Typography>
          {step.media.map((item, i) => {
            const isDark = theme.palette.mode === 'dark';
            const src = (isDark && item.src_darkmode) ? item.src_darkmode : item.src;
            const isVideo = VIDEO_EXT.test(src);
            return (
              <Box key={i} sx={{ mb: 1.5 }}>
                {isVideo ? (
                  <video src={src} controls style={{ maxWidth: '100%', borderRadius: 4 }} />
                ) : (
                  <img src={src} alt={item.caption || ''} style={{ maxWidth: '100%', borderRadius: 4 }} />
                )}
                {item.caption && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {item.caption}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default StepDetailPanel;
